import axios from "axios";

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BASE_URL}/api/`,
  headers: {
    "Content-Type": "application/json",
  },
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Optional: request interceptor (token)
api.interceptors.request.use(
  (config) => {
    // Get token from authState stored in localStorage
    const authState = localStorage.getItem("authState");
    if (authState) {
      try {
        const parsedAuth = JSON.parse(authState);

        const token = parsedAuth?.user?.token;
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Optional: response interceptor with refresh token logic
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 403) {
      window.alert("Your session has expired or you do not have permission. You will be redirected to the login page.");

      try {
        const authState = localStorage.getItem("authState");
        if (authState) {
          const parsedAuth = JSON.parse(authState);
          const token = parsedAuth?.user?.token;
          if (token) {
            await axios.post(
              `${process.env.REACT_APP_BASE_URL}/api/user-service/logout`,
              {},
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            ).catch(() => { });
          }
        }
      } catch (e) {
        console.error("Error during logout on 403:", e);
      }

      localStorage.removeItem("authState");
      window.location.href = "/login";
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const authState = localStorage.getItem("authState");
        if (!authState) {
          throw new Error("No auth state found");
        }

        const parsedAuth = JSON.parse(authState);
        const refreshToken = parsedAuth?.user?.refresh_token;

        if (!refreshToken) {
          throw new Error("No refresh token available");
        }

        // Call refresh token endpoint
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/api/user-service/refresh-token`,
          {
            refresh_token: refreshToken,
          }
        );

        const newToken = response.data?.data?.token;
        const newRefreshToken = response.data?.data?.refresh_token;

        if (!newToken) {
          throw new Error("No token in refresh response");
        }

        // Update localStorage with new tokens
        const updatedAuth = {
          ...parsedAuth,
          user: {
            ...parsedAuth.user,
            token: newToken,
            refresh_token: newRefreshToken || refreshToken,
          },
        };
        localStorage.setItem("authState", JSON.stringify(updatedAuth));

        // Update the original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        processQueue(null, newToken);
        isRefreshing = false;

        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;
        // Clear auth state and redirect to login
        localStorage.removeItem("authState");

        // Redirect to login page
        window.location.href = "/login";

        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
